import { Component, inject } from '@angular/core';
import { EpTableComponent, EPTableConfig } from '@ep/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, scan, startWith, switchMap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { Representative } from '@shared/models';
import { RepresentativeTypes } from '@shared/enums';
import { RepresentativesApiService } from '@app/areas/admin/representatives/services/representatives.api.service';

@Component({
  selector: 'app-representative-table',
  templateUrl: './representative-table.component.html',
  styleUrls: ['./representative-table.component.scss'],
  standalone: true,
  imports: [EpTableComponent, AsyncPipe],
})
export class RepresentativeTableComponent {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly representativesApiService = inject(RepresentativesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected config: EPTableConfig<Representative> = {
    header: 'representatives',
    pagination: {
      rows: 10,
      showCurrentPageReport: true,
    },
    columns: [
      {
        header: 'First Name',
        field: 'NameFirst',
        columnType: 'string',
        defaultValue: '',
      },
      {
        header: 'Last Name',
        field: 'NameLast',
        columnType: 'string',
        defaultValue: '',
      },
      {
        header: 'Email',
        field: 'EmailAddress',
        columnType: 'string',
        defaultValue: '',
      },
    ],
    style: {
      'min-width': '50rem',
      border: '5px',
    },
    globalFilterFields: ['NameFirst', 'NameLast', 'EmailAddress'],
    actions: {
      addNew: () => {
        this.router.navigate(['new'], { relativeTo: this.route });
      },
      rowClick: (representative: Representative) => {
        this.router.navigate([representative.RepresentativeId], { relativeTo: this.route });
      },
    },
  };

  protected representatives$: Observable<Representative[]> = this.applicationProfileService.applicationProfile$.pipe(
    switchMap((appProfile) => {
      const coalitionId = appProfile.Representative.CoalitionId;
      return this.representativesApiService.getByCoalitionId(coalitionId).pipe(
        switchMap((initialRepresentatives) =>
          this.representativesApiService.representativeChanged$.pipe(
            filter((representative) => representative.CoalitionId === coalitionId),
            scan(upsertRepresentative, initialRepresentatives),
            startWith(initialRepresentatives)
          )
        ),
        map((representatives) =>
          representatives.filter(
            (representative) => !isSuperAdmin(representative) && !hasOnlyFreeTierMerchants(representative)
          )
        )
      );
    })
  );
}

function isSuperAdmin(representative: Representative): boolean {
  return ((representative.RepresentativeType ?? 0) & RepresentativeTypes.SuperAdmin) === RepresentativeTypes.SuperAdmin;
}

/** Reps whose assigned merchants are all free tier are hidden; reps with no merchants stay visible. */
function hasOnlyFreeTierMerchants(representative: Representative): boolean {
  return representative.HasOnlyFreeTierMerchants === true;
}

function upsertRepresentative(representatives: Representative[], changed: Representative): Representative[] {
  const index = representatives.findIndex(
    (representative) => representative.RepresentativeId === changed.RepresentativeId
  );

  if (index === -1) {
    return [...representatives, changed];
  }

  const updated = [...representatives];
  updated[index] = changed;
  return updated;
}
